import CustomDropDownMenu from "@components/FormFields/CustomDropDownMenu";
import { MusicButton } from "@components/MusicGrid";
import NoElementFoundContainer from "@components/NoElementsFoundContainer";
import AssetPlayer from "@components/Players/AssetPlayer";
import LayoutPlayer from "@components/Players/LayoutPlayer";
import PlaylistGrid from "@components/Players/PlaylistGrid";
import QueryFetchStatePage from "@components/QueryFetchStatePage";
import AddMusicZoneAddOnDialog from "@components/screens/AddMusicZoneAddOnDialog";
import AddScreenAddOnDialog from "@components/screens/AddScreenAddOnDialog";
import { ScreenshotDialog } from "@components/screens/ScreenshotDialog";
import { ScreenIdType, screenTypes } from "@components/screens/data";
import { EditMusicStatus } from "@components/screens/EditMusicStatus";
import SignageBillingProvider from "@components/shared/Billing/SignageBillingProvider";
import CustomHead from "@components/shared/CustomHead";
import CustomTable, {
  ColumnDef,
  useTable,
} from "@components/shared/CustomTable";
import { PermissionGate, usePermissionRender } from "@components/shared/PermissionGate";
import VideoPreview from "@components/shared/VideoPreview";
import SideBarPage from "@components/SideBar/sidebarPage";
import { Button } from "@components/ui/button";
import { FlagComponent } from "@components/ui/phone-input";
import { useAssets } from "@hooks/useAssets";
import { useAuth } from "@hooks/useAuth";
import useDialog, { DialogProvider } from "@hooks/useDialog";
import useGzLog from "@hooks/useGzLog";
import { useLayouts } from "@hooks/useLayouts";
import { useLocations } from "@hooks/useLocations";
import { PlayingState, useGlobalMusic, useMusic } from "@hooks/useMusic";
import { usePlans } from "@hooks/usePlans";
import { usePlaylists } from "@hooks/usePlaylists";
import { usePublicAssets } from "@hooks/usePublicAssets";
import { usePublicPlaylists } from "@hooks/usePublicPlaylists";
import { usePublicTemplates } from "@hooks/usePublicTemplates";
import { usePublicVideos } from "@hooks/usePublicVideos";
import { ScreenProvider, useScreen } from "@hooks/useScreen";
import { ScreenWithPlayingInfo, useContentSelectorDialog, useScreens } from "@hooks/useScreens";
import { Screen, ScreenPlayingOptionType } from "@hooks/useScreens/types";
import { useSelectMusicDialog } from "@hooks/useSelectMusicDialog";
import { useTemplates } from "@hooks/useTemplates";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import { useVideoAssets } from "@hooks/useVideoAssets";
import { useContentPreviewDialog } from "@components/shared/ContentPreviewDialog";
import { env } from "@src/env.mjs";
import { useQueryClient } from "@tanstack/react-query";
import { ExpandedState, getCoreRowModel, getExpandedRowModel, getGroupedRowModel, GroupingState } from "@tanstack/react-table";
import { useMusicZone, useUnpairScreenQuery } from "@utils/queries";
import { supabase } from "@utils/serverClient";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { BiLocationPlus } from "react-icons/bi";
import { FiMonitor } from "react-icons/fi";
import {
  MdDelete,
  MdEdit,
  MdInfo, MdKeyboardDoubleArrowDown, MdKeyboardDoubleArrowUp, MdLink,
  MdLinkOff,
  MdOutlineOpenInNew,
  MdRestartAlt,
  MdVideoLibrary,
  MdCamera,
  MdOpenInNew,
  MdVisibility
} from "react-icons/md"; // Add this import at the top with other icon imports
import { type Country } from "react-phone-number-input";
import countryNames from "react-phone-number-input/locale/en";
import AddScreenDialog from "../../components/screens/AddScreenDialog";
import { useRouter } from "next/router";
import { URLEncoder } from "@utils/urlEncoder";
import { MusicChannel } from "@types/MusicApi";
import { Asset, Layout, Playlist, Template, Video } from "@types/index";

const CountryCode = {
  fromName(name: string) {
    return Object.keys(countryNames).find(key => countryNames[key as keyof countryNames] === name) as Country | undefined
  }
}

function ScreensPage() {
  return (
    <div>
      <CustomHead title="Screens" />

      <SideBarPage>
        <ScreensPageBody />
      </SideBarPage>
    </div>
  );
}

function ScreensPageBody() {
  const { profileFetchStatus, organization, getProfile } = useAuth();

  return (
    <QueryFetchStatePage
      queryFetchStatus={profileFetchStatus}
      onRetry={getProfile}
    >
      <>
        {organization?.id && (
          <SignageBillingProvider>
            <DialogProvider>
              <ResolvedScreensPageBody org_id={organization.id} />
            </DialogProvider>
          </SignageBillingProvider>
        )}
      </>
    </QueryFetchStatePage>
  );
}

function ResolvedScreensPageBody({ org_id }: { org_id: string }) {
  const { locationsFetchStatus, getLocations } = useLocations()
  const { refetchMusic, musicFetchStatus } = useMusic()

  return (
    <QueryFetchStatePage
      queryFetchStatus={locationsFetchStatus}
      onRetry={() => getLocations()}
    >
      <QueryFetchStatePage
        queryFetchStatus={musicFetchStatus}
        onRetry={() => refetchMusic()}
      >
        <ScreensPagePresenter org_id={org_id} />
      </QueryFetchStatePage>
    </QueryFetchStatePage>
  );
}

function ScreensPagePresenter({ org_id }: { org_id: string }) {
  const {
    openDialog,
    openLoadingOverlay,
    closeDialog,
    showSuccessSnackbar,
    showErrorSnackbar,
  } = useDialog();

  const { user } = useAuth();

  const { playingStates } = useGlobalMusic();

  const {
    screens,
    screensFetchStatus,
    getScreens,
    emitEvent,
  } = useScreens();

  const { locations } = useLocations();
  const { getMusicCost } = usePlans()
  const { wherePermitted } = usePermissionRender()

  // Hooks needed for ScreenActionsDropdown
  const { openContentPreviewDialog } = useContentPreviewDialog();
  const { updateScreenPlaying } = useScreens();
  const { musicChannels } = useMusic();
  const { assets: _assets } = useAssets();
  const { assets: publicAssets } = usePublicAssets();
  const { layouts } = useLayouts();
  const { playlists: _playlists } = usePlaylists();
  const { playlists: publicPlaylists } = usePublicPlaylists();
  const { templates: _templates } = useTemplates();
  const { templates: publicTemplates } = usePublicTemplates();
  const { assets: _videos } = useVideoAssets();
  const { Videos: publicVideos } = usePublicVideos();

  const assets = [..._assets, ...publicAssets];
  const playlists = [..._playlists, ...publicPlaylists];
  const templates = [..._templates, ...publicTemplates];
  const videos = [..._videos, ...publicVideos];

  const columns = useMemo(
    () => ([
      {
        accessorKey: "location_id",
        cell: (info) => {
          const locationId = info.getValue()
          const location = locations.find(l => l.id === locationId)
          const countryCode = CountryCode.fromName(location?.country ?? "")

          return <div className="flex flex-row gap-3 max-w-[5.5rem]">
            <h3 className="capitalize flex flex-row gap-1 items-center rounded-md">
              {countryCode &&
                <FlagComponent country={countryCode} countryName="" />
              }
              {location?.name ?? "Others"}
            </h3>
          </div>;
        },
        header: ({ table }) => (
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-transparent"
            onClick={() => table.toggleAllRowsExpanded()}
          >
            <div className="flex items-center gap-2">
              {table.getIsAllRowsExpanded() ? (
                <MdKeyboardDoubleArrowUp className="h-5 w-5" />
              ) : (
                <MdKeyboardDoubleArrowDown className="h-5 w-5" />
              )}
              <span className="text-sm font-medium">
                {table.getIsAllRowsExpanded() ? 'Collapse All' : 'Expand All'}
              </span>
            </div>
          </Button>
        ),
      },
      {
        accessorKey: "name",
        cell: (info) => {
          if (user?.id === info.row.original.id)
            return (
              <div className="flex w-40 items-center justify-center gap-2 rounded-full bg-primary py-1.5 text-sm text-primary-foreground">
                <FiMonitor />
                Default Screen
              </div>
            );

          return info.getValue();
        },
        header: () => <span>Name</span>,
      },

      {
        accessorFn: (val) =>
          val.width === null || val.height === null
            ? "-"
            : `${val.width}x${val.height}`,
        id: "dimensions",
        cell: (info) => info.getValue(),
        header: () => <span>Size</span>,
      },

      {
        accessorKey: "screen_type",
        cell: (info) => {
          const screen = info.row.original;
          const _type = screenTypes.find((s) => s.id === screen.screen_type);

          return (
            <div className="flex">
              <div className="rounded-full bg-accentColor bg-opacity-5 px-4 py-2 text-xs text-accentColor dark:bg-darkAccentColor dark:bg-opacity-5 dark:text-darkAccentColor">
                {_type?.title ?? "Browser"}
              </div>
            </div>
          );
        },
        header: () => <span>Screen Type</span>,
      },
      wherePermitted({
        id: "now_playing",
        header: () => <span>Now Playing</span>,
        cell: (info) => {
          const screen = info.row.original;

          return <ScreenProvider screen={screen}>
            <ContentButton
              screenId={screen.id}
              locationId={screen.location_id}
              screens={screens}
              musicChannels={musicChannels}
              playingStates={playingStates}
              assets={assets}
              playlists={playlists}
              templates={templates}
              videos={videos}
              layouts={layouts}
            />
          </ScreenProvider>
        },
      }, ps => ps.includes("screens:control_content") || ps.includes("screens:control_music")),
      {
        id: "music_status",
        header: () => <span>Music Status</span>,
        cell: (info) => {
          const screen = info.row.original;
          if (!screen.music_integration_enabled) return "-";

          return (
            <>
              {user?.system_role === 'admin' && screen.zone_id && (
                <EditMusicStatus screen={screen} />
              )}
            </>
          );
        },
      },
      {
        id: "controls",
        header: () => (
          <div className="inline-flex w-full items-center justify-end">
            Actions
          </div>
        ),
        cell: (info) => {
          const screen = info.row.original;

          return (
            <div className="flex items-center justify-end gap-2">
              {
                user?.id === screen.id ?
                  <Button
                    size="sm"
                    variant={"secondary"}
                    onClick={() => {
                      log({
                        name: "OpenedScreen",
                        eventtype: "Action",
                        payload: {
                          screenId: screen.id,
                          screenName: screen.name ?? "undefined",
                        },
                      });
                      window.open(env.NEXT_PUBLIC_PLAYER_URL, "_blank");
                    }}
                  >
                    <MdOutlineOpenInNew />
                    Open Screen
                  </Button> :
                  !screen.is_paired && (
                    <Button
                      size="sm"
                      variant={"secondary"}
                      onClick={() => {
                        log({
                          name: "PairScreenClicked",
                          eventtype: "Action",
                          payload: {
                            screenId: screen.id,
                            screenName: screen.name ?? "undefined",
                          },
                        });
                        openScreenAddDialog(screen, true);
                      }}
                    >
                      <MdLink />
                      Pair Now
                    </Button>
                  )}

              {screen.is_paired && <OnlineStatus screenId={screen.id} />}
              <ScreenActionsDropdown
                screen={screen}
                openDeleteScreenDialog={openDeleteScreenDialog}
                openScreenAddDialog={openScreenAddDialog}
                openScreenshotDialog={openScreenshotDialog}
                openContentPreviewDialog={openContentPreviewDialog}
                updateScreenPlaying={updateScreenPlaying}
                screens={screens}
                musicChannels={musicChannels}
                playingStates={playingStates}
                assets={assets}
                playlists={playlists}
                templates={templates}
                videos={videos}
                layouts={layouts}
              />
            </div>
          );
        },
      },
    ] as ColumnDef<ScreenWithPlayingInfo>[]).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screens, playingStates],
  );

  const groupingRef = useRef<GroupingState>(["location_id"]);
  const [expanded, setExpanded] = useState<ExpandedState>(
    () => JSON.parse(localStorage.getItem('screensTableExpanded') ?? 'true')
  );

  useEffect(() => {
    localStorage.setItem('screensTableExpanded', JSON.stringify(expanded));
  }, [expanded]);

  const { table } = useTable({
    data: screens,
    columns,
    getRowId: (row) => row.id,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange: setExpanded,
    autoResetExpanded: false,
    state: {
      grouping: groupingRef.current,
      expanded,
      columnVisibility: {
        music_status: user?.system_role === "admin",
      }
    }
  });

  const { planProperties, subscription } = useUserSubscriptions();

  const { mutate: unpairScreen } = useUnpairScreenQuery();
  const { openContentSelectorDialog } = useContentSelectorDialog()
  const { log } = useGzLog();

  // State for screenshot dialog
  const [screenshotDialogOpen, setScreenshotDialogOpen] = useState(false);
  const [selectedScreenshotScreen, setSelectedScreenshotScreen] = useState<Screen | null>(null);

  function openScreenAddDialog(screen?: Screen, isPairAgain?: boolean) {
    openDialog({
      showActions: false,
      customContent: (
        <DialogProvider>
          <AddScreenDialog
            screen={screen}
            isPairAgain={isPairAgain}
            org_id={org_id}
            onSuccess={(newScreen) => {
              // closeDialog()
              openContentSelectorDialog(newScreen);
            }}
          />
        </DialogProvider>
      ),
    });
  }

  // Function to open screenshot dialog
  function openScreenshotDialog(screen: Screen) {
    setSelectedScreenshotScreen(screen);
    setScreenshotDialogOpen(true);
  }

  const queryClient = useQueryClient()
  const { mutateAsync: deleteMusicZone } = useMusicZone.delete()

  function openDeleteScreenDialog(screenId: string, zoneId?: number | null, softDelete = false) {
    const baseCost = getMusicCost();
    const screen = screens.find(s => s.id === screenId);

    const unpairText = softDelete ? "remove" : "unpair"

    const message = screen?.music_integration_enabled && baseCost > 0
      ? `Are you sure you want to ${unpairText} "${screen.name}"? You will no longer be charged $${baseCost}/month for music integration on this screen.`
      : `Are you sure you want to ${unpairText} "${screen?.name}"?`;

    openDialog({
      title: `${unpairText.charAt(0).toUpperCase() + unpairText.slice(1)} Screen`,
      description: message,
      dialogType: "confirm",
      onConfirm: async () => {
        openLoadingOverlay();

        unpairScreen(
          { screenId: screenId, softDelete },
          {
            async onSuccess() {
              emitEvent("screenChanged", screenId, {
                new: {
                  deleted: true,
                },
              });
              if (zoneId) {
                // Get all screens in the location if this is a controller
                const screens = queryClient.getQueryData(["screens", org_id]) as ScreenWithPlayingInfo[] | undefined;
                if (screens) {
                  const currentScreen = screens.find(s => s.id === screenId);
                  if (currentScreen?.location_id) {
                    const isController = screens.find(s => s.id === screenId)?.group_id === null;
                    const affectedScreens = isController ?
                      screens.filter(s => s.location_id === currentScreen.location_id && !!s.group_id) :
                      [currentScreen];

                    // Update local state immediately
                    queryClient.setQueryData(["screens", org_id], (oldScreens: ScreenWithPlayingInfo[] | undefined) => {
                      return oldScreens?.map(s => {
                        if (affectedScreens.some(affected => affected.id === s.id)) {
                          return {
                            ...s,
                            music_integration_enabled: s.id === currentScreen.id ? false : s.music_integration_enabled,
                            group_id: null,
                            playlist_id: null,
                            music_playing: false,
                            last_music_id: null,
                            group_music_infos: null
                          }
                        }
                        return s
                      })
                    })

                    // Update screens to remove music sync
                    await supabase
                      .from('screen')
                      .update({ group_id: null, playlist_id: null, music_playing: false, last_music_id: null, group_music_infos: null })
                      .in('id', affectedScreens.map(s => s.id))
                      .eq('location_id', currentScreen.location_id);

                    // Delete music control records
                    await supabase
                      .from('music_control')
                      .delete()
                      .in('screen_id', affectedScreens.map(s => s.id))
                      .eq('location_id', currentScreen.location_id);
                  }
                }
                deleteMusicZone({ id: zoneId }, {
                  onSuccess() {
                    queryClient.invalidateQueries({ queryKey: ["screens", org_id] });
                  }
                });
              }
            },
          },
        );
        closeDialog();
      }
    });
  }

  function loadedBody() {
    if (!screens?.length)
      return (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <div className="w-[550px]">
            <DialogProvider>
              <AddScreenDialog
                isFirstScreen
                org_id={org_id}
                onSuccess={(newScreen) => {
                  openContentSelectorDialog(newScreen);
                }}
              />
            </DialogProvider>
          </div>
        </div>
      );

    const maxScreens =
      planProperties.max_screens ?? planProperties.entitled_screens;

    return (
      <div className="flex h-full w-full flex-col">
        <div className="flex items-center justify-between">
          <div className="flex gap-2 text-xl font-semibold">
            <h1>Screens</h1>

            {maxScreens && (
              <div
                className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${screens.length - 1 >= maxScreens
                  ? "bg-red-500/10 text-red-500"
                  : "bg-primary/20  text-primary"
                  }`}
              >
                <p>
                  {`${screens.length - 1 >= maxScreens
                    ? "No more screens available"
                    : `${Math.abs(
                      screens.length - 1 - maxScreens,
                    )} screens available`
                    } `}
                </p>
                <div
                  className="tooltip tooltip-bottom"
                  data-tip={`Total screens available: ${maxScreens}`}
                >
                  <MdInfo />
                </div>
              </div>
            )}
          </div>
          <PermissionGate where={ps => ps.includes("screens:create")}>
            <Button
              onClick={() => {
                const screensLength = screens.length - 1;
                if (
                  planProperties.max_screens &&
                  screensLength >= planProperties.max_screens
                ) {
                  openDialog({
                    title: "Max Screens Reached",
                    customContent: (
                      <div className="flex flex-col gap-2 pt-2">
                        <p>
                          {subscription?.plan?.name} only allows upto{" "}
                          {planProperties.max_screens}{" "}
                          screens. Please upgrade your plan.
                        </p>

                        <div className="flex w-full items-center justify-end">
                          <Button asChild>
                            <Link href={"/change_plan"}>Upgrade Options</Link>
                          </Button>
                        </div>
                      </div>
                    ),
                  });
                } else if (
                  planProperties.entitled_screens &&
                  planProperties.addon_screen_cost &&
                  screensLength >= planProperties.entitled_screens
                ) {
                  openDialog({
                    title: "Max Screens Reached",
                    showActions: false,
                    customContent: (
                      <DialogProvider>
                        <AddScreenAddOnDialog onClose={closeDialog} />
                      </DialogProvider>
                    ),
                  });
                } else {
                  openScreenAddDialog(undefined);
                }
              }}
            >
              <BiLocationPlus />
              Add Screen
            </Button>
          </PermissionGate>
        </div>
        <PermissionGate has="screens:view">
          {screens.length > 0 ? (
            <CustomTable table={table} totalRows={screens.length} />
          ) : (
            <NoElementFoundContainer message="No Screens added yet." />
          )}
        </PermissionGate>

        {/* Screenshot Dialog */}
        {selectedScreenshotScreen && (
          <ScreenshotDialog
            screen={selectedScreenshotScreen}
            isOpen={screenshotDialogOpen}
            onClose={() => setScreenshotDialogOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <QueryFetchStatePage
      queryFetchStatus={screensFetchStatus}
      onRetry={getScreens}
    >
      <>{loadedBody()}</>
    </QueryFetchStatePage>
  );
}

function ScreenActionsDropdown({
  screen,
  openDeleteScreenDialog,
  openScreenAddDialog,
  openScreenshotDialog,
  openContentPreviewDialog,
  updateScreenPlaying,
  screens,
  musicChannels,
  playingStates,
  assets,
  playlists,
  templates,
  videos,
  layouts,
}: {
  screen: ScreenWithPlayingInfo;
  openScreenAddDialog: (screen?: Screen, isPairAgain?: boolean) => void;
  openDeleteScreenDialog: (screenId: string, zoneId?: number | null, softDelete?: boolean) => void;
  openScreenshotDialog: (screen: Screen) => void;
  openContentPreviewDialog: (props: any) => void;
  updateScreenPlaying: any;
  screens: ScreenWithPlayingInfo[];
  musicChannels: any[];
  playingStates: any;
  assets: any[];
  playlists: any[];
  templates: any[];
  videos: any[];
  layouts: any[];
}) {
  const { ifPermitted, ifPermittedAnd } = usePermissionRender()
  const { onlineStatus, emitEvent } = useScreens();
  const { user } = useAuth();
  const { closeDialog } = useDialog();
  const { log } = useGzLog();
  const org_id = user?.org_id
  const router = useRouter();

  // Determine the currently playing content
  let currentPlaying: ScreenPlayingOptionType | undefined = undefined;
  const _playingMusic = !screen.music_integration_enabled ? undefined : musicChannels?.find((m: any) => m.id === (playingStates[screen.id]?.playlistId ?? screen.last_music_id));

  if (screen.asset_id) {
    const asset = assets.find((a: any) => a.id === screen.asset_id)
    if (asset)
      currentPlaying = {
        type: "asset",
        ...asset,
      };
  } else if (screen.layout_id) {
    const layout = layouts.find((l: any) => l.id === screen.layout_id)
    if (layout)
      currentPlaying = {
        type: "layout",
        ...layout,
      };
  } else if (screen.playlist_id) {
    const playlist = playlists.find((p: any) => p.id === screen.playlist_id)
    if (playlist)
      currentPlaying = {
        type: "playlist",
        ...playlist,
      };
  } else if (screen.template_id) {
    const template = templates.find((t: any) => t.id === screen.template_id)
    if (template)
      currentPlaying = {
        type: "template",
        ...template,
      };
  } else if (screen.video_id) {
    const video = videos.find((v: any) => v.id === screen.video_id)
    if (video)
      currentPlaying = {
        type: "video",
        ...video,
      };
  }

  // Function to open the content preview dialog for editing
  function openEditCurrentLayout() {
    if (!currentPlaying || !user?.id || !org_id) return;

    // Only allow editing for layouts and playlists
    if (currentPlaying.type !== "layout" && currentPlaying.type !== "playlist") return;

    openContentPreviewDialog({
      opt: currentPlaying,
      onChange: (val: ScreenPlayingOptionType, openInNew: boolean) => {
        if (openInNew) {
          window.open(`/layout?id=${val.id}`, "_blank");
        }
      },
      updateScreenPlaying: updateScreenPlaying,
      screen: screen,
      screens: screens,
      duration: undefined,
      orgId: org_id,
      userId: user.id,
      imageFit: screen.image_fit,
      closeInitialDialog: closeDialog,
    });
  }

  return (
    <CustomDropDownMenu
      menuItems={[
        {
          items: [
            screen.is_paired &&
            onlineStatus[screen.id] && {
              icon: <MdRestartAlt />,
              title: "Restart",
              onClick() {
                emitEvent("restart", screen.id);
                log({
                  eventtype: "ScreenRestarted",
                  name: "ScreenRestarted",
                  payload: {
                    screenId: screen.id,
                    screenName: screen.name ?? "undefined",
                    userId: user?.id,
                    organizationId: org_id ?? "undefined",
                  },
                });
              },
            },
            ifPermitted("screens:update_settings", {
              icon: <MdEdit />,
              title: "Edit",
              onClick() {
                openScreenAddDialog(screen);
              },
            }),
            ifPermittedAnd("screens:control_layout", () => {
              return currentPlaying?.type === "layout" || currentPlaying?.type === "template"
            }, {
              icon: <MdVisibility />,
              title: "View Current Layout",
              onClick: openEditCurrentLayout,
              disabled: !currentPlaying || (currentPlaying.type !== "layout" && currentPlaying.type !== "playlist"),
            }),
            ifPermitted("screens:view", {
              icon: <MdOpenInNew />,
              title: "Preview in Browser",
              onClick() {
                const previewParams = new URLSearchParams();

                const deviceId = screen.id
                previewParams.append("did", URLEncoder.encode(deviceId))
                previewParams.append("frame", "false")

                const previewUrl = `${env.NEXT_PUBLIC_PLAYER_URL}?${previewParams.toString()}`;
                window.open(previewUrl, '_blank');
              },
            }),
            screen.id === user?.id || !screen.is_paired ? undefined :
              ifPermitted("screens:delete", {
                icon: <MdLinkOff />,
                title: "Unpair",
                onClick() {
                  openDeleteScreenDialog(screen.id, screen.zone_id);
                },
              }),
            ifPermitted("screens:delete", {
              icon: <MdDelete />,
              title: "Delete",
              onClick() {
                openDeleteScreenDialog(screen.id, screen.zone_id, true);
              },
            }),
            screen.is_paired &&
            onlineStatus[screen.id] &&
            ifPermitted("screens:control_layout", {
              icon: <MdCamera />,
              title: "Screenshot",
              onClick() {
                openScreenshotDialog(screen);
              },
            }),
          ].filter(Boolean),
        },
      ]}
    />
  );
}

function _optionRenderer(opt: ScreenPlayingOptionType) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-6 w-6 items-center justify-center overflow-clip">
        {opt.type === "asset" && (
          <AssetPlayer asset={opt} />
        )}

        {opt.type === "layout" || opt.type === "template" && <LayoutPlayer layoutInfo={opt} muted key={opt.id} />}
        {opt.type === "playlist" && <PlaylistGrid
          playlist={opt}
          key={opt.id} />}
        {opt.type === "video" && <VideoPreview videoUrl={opt.url ?? ""}>
          {
            opt.thumbnail ? <img src={opt.thumbnail} alt={opt.name ?? "video"} className="size-12" />
              : <div className="flex h-full w-full items-center justify-center text-lg font-semibold">
                <MdVideoLibrary />
              </div>
          }
        </VideoPreview>}
      </div>
      {/* <p className="max-w-[100px] flex-1 truncate">{opt.label}</p> */}
      <p className="max-w-[100px] flex-1 truncate capitalize">{opt.type}</p>
    </div>
  );
}

function OnlineStatus({ screenId }: { screenId: string }) {
  const { onlineStatus } = useScreens();
  const isOnline = onlineStatus[screenId];

  return (
    <div className="flex items-center justify-center gap-2 rounded-full bg-muted px-3 py-1 text-xs">
      <div
        className={`h-3 w-3 rounded-full ${isOnline ? "bg-green-500" : "bg-red-500"
          }`}
      ></div>
      {isOnline ? "Online" : "Offline"}
    </div>
  );
}

function ContentButton({
  screenId,
  locationId,
  screens,
  musicChannels,
  playingStates,
  assets,
  playlists,
  templates,
  videos,
  layouts
}: {
  screenId: string,
  locationId: string | null,
  screens: ScreenWithPlayingInfo[],
  musicChannels: MusicChannel[],
  playingStates: Record<string, PlayingState>,
  assets: Asset[],
  playlists: Playlist[],
  templates: Template[],
  videos: Video[],
  layouts: Layout[]
}) {
  const { openDialog, closeDialog } = useDialog()
  const { openContentSelectorDialog } = useContentSelectorDialog()
  const { planProperties, subscription } = useUserSubscriptions()
  const { screenInfo } = useScreen()
  const screen = screens.find(s => s.id === screenInfo?.id)!
  const { openSelectMusicDialog } = useSelectMusicDialog()

  let _playing: ScreenPlayingOptionType | undefined = undefined;
  const _playingMusic = !screen.music_integration_enabled ? undefined : musicChannels?.find((m: any) => m.id === (playingStates[screen.id]?.playlistId ?? screen.last_music_id))

  if (screen.asset_id) {
    const asset = assets.find((a) => a.id === screen.asset_id)
    if (asset)
      _playing = {
        type: "asset",
        ...asset,
      };
  } else if (screen.layout_id) {
    const layout = layouts.find((l) => l.id === screen.layout_id)
    if (layout)
      _playing = {
        type: "layout",
        ...layout,
      };
  } else if (screen.playlist_id) {
    const playlist = playlists.find((p) => p.id === screen.playlist_id)
    if (playlist)
      _playing = {
        type: "playlist",
        ...playlist,
      };
  } else if (screen.template_id) {
    const template = templates.find((t) => t.id === screen.template_id)
    if (template)
      _playing = {
        type: "template",
        ...template,
      };
  } else if (screen.video_id) {
    const video = videos.find((v) => v.id === screen.video_id)
    if (video)
      _playing = {
        type: "video",
        ...video,
      };
  }

  function getMusicButtonVariant() {
    return screen.music_integration_enabled && (playingStates[screen.id] ? playingStates[screen.id].state === "playing" : screen.music_playing) ? "filled" : "outline"
  }

  function onMusicButtonClick() {
    const musicZoneLength = screens.filter(s => s.music_integration_enabled).length;
    if (
      planProperties.max_music_zones &&
      musicZoneLength >= planProperties.max_music_zones
    ) {
      openDialog({
        title: "Max Music Integration Reached",
        customContent: (
          <div className="flex flex-col gap-2 pt-2">
            <p>
              {subscription?.plan?.name} only allows upto{" "}
              {planProperties.max_music_zones}{" "}
              music integrations. Please upgrade your plan.
            </p>

            <div className="flex w-full items-center justify-end">
              <Button asChild>
                <Link href={"/change_plan"}>Upgrade Options</Link>
              </Button>
            </div>
          </div>
        ),
      });
    } else if (
      planProperties.entitled_music_zones &&
      planProperties.addon_screen_cost &&
      musicZoneLength >= planProperties.entitled_music_zones
    ) {
      openDialog({
        title: "Max Music Integration Reached",
        showActions: false,
        customContent: (
          <DialogProvider>
            <AddMusicZoneAddOnDialog onClose={closeDialog} />
          </DialogProvider>
        ),
      });
    } else {
      openSelectMusicDialog({ screen });
    }
  }

  return (
    <div className="flex items-center space-x-4">
      <PermissionGate has="screens:control_layout">
        <Button
          size="sm"
          variant={"outline"}
          onClick={() => {
            openContentSelectorDialog(screen);
          }}
        >
          {!_playing ? <>Select content</> : _optionRenderer(_playing)}
        </Button>
      </PermissionGate>
      <PermissionGate has="music:view">
        {
          <div className="group relative flex items-center rounded-md border border-input w-fit overflow-hidden">
            <MusicButton playingMusicName={_playingMusic?.name} musicIntegrationEnabled={screen.music_integration_enabled ?? false} onClick={onMusicButtonClick} variant={getMusicButtonVariant()} locationId={locationId} screen={screen} />
          </div>
        }
      </PermissionGate>
    </div>
  );
}

export default ScreensPage;
